import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, UserCircleIcon, ArrowRightOnRectangleIcon, CogIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

const getInitials = (name, role) => {
 if (!name) return role === 'student' ? 'ST' : 'UN';
 const parts = name.trim().split(/\s+/);
 if (parts.length >= 2) {
 return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
 }
 return name.slice(0, 2).toUpperCase();
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);
  const { user, logout, isAuthenticated, isStudent, isUniversity } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const closeMenu = () => {
    setIsOpen(false);
    setShowMobileProfileMenu(false);
  };

 useEffect(() => {
 const updateHeight = () => {
 if (navRef.current) {
 const height = navRef.current.getBoundingClientRect().height;
 document.documentElement.style.setProperty('--navbar-height', `${height}px`);
 }
 };

 updateHeight();

 if (typeof ResizeObserver !== 'undefined' && navRef.current) {
 const resizeObserver = new ResizeObserver(() => {
 updateHeight();
 });
 resizeObserver.observe(navRef.current);
 return () => {
 resizeObserver.disconnect();
 };
 } else {
 window.addEventListener('resize', updateHeight);
 return () => {
 window.removeEventListener('resize', updateHeight);
 };
 }
 }, [showWarning]);

 useEffect(() => {
 const handleClickOutside = (event) => {
 if (navRef.current && !navRef.current.contains(event.target)) {
 setIsOpen(false);
 setShowProfileMenu(false);
 }
 };

 document.addEventListener('mousedown', handleClickOutside);
 document.addEventListener('touchstart', handleClickOutside);
 return () => {
 document.removeEventListener('mousedown', handleClickOutside);
 document.removeEventListener('touchstart', handleClickOutside);
 };
  }, []);

  // Swipe gesture listeners for mobile drawer (swipe right to close, swipe left from edge to open)
  useEffect(() => {
    const swipeThreshold = 50; // pixels
    const edgeThreshold = 40;  // pixels from right edge to trigger swipe-to-open

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e) => {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;

      // Ensure gesture is horizontal
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (isOpen) {
          // Collapse on right-swipe (finger moves left-to-right)
          if (deltaX > swipeThreshold) {
            setIsOpen(false);
          }
        } else {
          // Open on left-swipe (finger moves right-to-left) starting from right edge
          const screenWidth = window.innerWidth;
          const startedAtEdge = (screenWidth - touchStartRef.current.x) < edgeThreshold;

          if (deltaX < -swipeThreshold && startedAtEdge) {
            setIsOpen(true);
          }
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen]);

 const handleLogout = () => {
 logout();
 navigate('/');
 setShowProfileMenu(false);
 setIsOpen(false);
 };

 const handleAdminLogout = () => {
 localStorage.removeItem('adminToken');
 navigate('/');
 setIsOpen(false);
 };

 const isActive = (path) => location.pathname === path;

 // Unified dynamic navigation links
 const navLinks = isUniversity
  ? [
  { name: 'Dashboard', path: '/dashboard' },
  ]
  : [
  { name: 'Universities', path: '/' },
  { name: 'Programs', path: '/programs' },
  { name: 'Direct Apply', path: '/direct-apply', comingSoon: true },
  { name: 'Blog', path: '/blog' },
  { name: 'Contact Us', path: '/contact' }
  ];

 return (
 <nav ref={navRef} className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm transition-premium">
 {/*
 ⚠️ TEMPORARY NOTE - REMOVE BEFORE PRODUCTION
 This warning note should be removed when the application goes live
 Currently showing on all devices for development/testing purposes
 */}
 {showWarning && (
 <div className="flex items-center pt-2">
 <div className="bg-amber-100 border border-amber-300 rounded-md px-3 py-1.5 mx-2 sm:mx-5 w-full flex items-center justify-between">
 <div className="flex-1 text-center">
 <span className="text-amber-900 text-xs sm:text-sm font-medium">
 ⚠️ NOTE: Information provided is for sample purposes only - not 100% accurate
 </span>
 </div>
 <button
 onClick={() => setShowWarning(false)}
 className="text-amber-800 hover:text-amber-900 focus:outline-none ml-2 shrink-0 p-0.5 rounded hover:bg-amber-200 transition-colors"
 aria-label="Dismiss note"
 >
 <XMarkIcon className="h-4 w-4" />
 </button>
 </div>
 </div>
 )}

 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 relative">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to={isUniversity ? "/dashboard" : "/"} className="flex items-center">
              <img 
                className="h-5 w-auto" 
                src="/assets/logos/dakhla-logo-english.svg" 
                alt="Dakhla" 
              />
            </Link>
          </div>

          {/* Center Navigation Links */}
          <div className="hidden md:flex items-center space-x-8 h-full absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative h-full flex items-center gap-1.5 px-1 text-sm transition-colors duration-200 ${
                  isActive(link.path) ? 'text-[#1E256F] font-bold' : 'text-slate-600 hover:text-[#1E256F] font-semibold'
                }`}
              >
                <span>{link.name}</span>
                {link.comingSoon && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wide border border-slate-200 leading-none">
                    Soon
                  </span>
                )}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#1E256F] rounded-t-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Action / Auth Buttons */}
          <div className="hidden md:flex items-center">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {/* Profile Menu Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center focus:outline-none transition-colors duration-200 cursor-pointer space-x-1.5 p-1 hover:bg-slate-50 rounded-full border border-transparent hover:border-slate-100"
                  >
                    {user?.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border border-slate-200 shadow-sm"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center border border-primary-100/30 text-primary-600 text-xs font-bold uppercase shadow-sm">
                        {getInitials(user?.name, user?.role)}
                      </div>
                    )}
                    <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                  </button>

                  <div className={`absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl py-1.5 border border-slate-200 z-50 transition-all duration-200 origin-top-right ${
                    showProfileMenu
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                  }`}>
                    <Link
                      to={isUniversity ? "/dashboard" : "/profile"}
                      className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <UserCircleIcon className="h-4 w-4 mr-2.5 text-slate-400" />
                      {isUniversity ? 'Dashboard' : 'Profile'}
                    </Link>
                    {isUniversity && (
                      <Link
                        to="/profile?tab=applicants"
                        className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <svg className="h-4 w-4 mr-2.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Application List
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <CogIcon className="h-4 w-4 mr-2.5 text-slate-400" />
                      Settings
                    </Link>
                    <div className="h-px bg-slate-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50/50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2.5 text-red-500" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2.5">
                {localStorage.getItem('adminToken') ? (
                  <div className="flex items-center space-x-3">
                    <Link to="/supad-panel/dashboard" className="btn-primary from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-md shadow-red-500/10 hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98] transition-premium cursor-pointer border-transparent py-2 px-5 text-sm font-bold rounded-xl">
                      Admin Panel
                    </Link>
                    <button
                      onClick={handleAdminLogout}
                      className="text-slate-500 hover:text-red-600 text-sm font-semibold px-2 py-1 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2.5">
                    <Link to="/login" className="border border-slate-300 text-slate-600 hover:text-[#1E256F] hover:bg-slate-50 hover:border-slate-300 text-sm px-5 py-2 font-bold rounded-xl active:scale-[0.98] transition-all duration-300">
                      Log In
                    </Link>
                    <Link to="/signup" className="bg-[#1E256F] hover:bg-[#161B52] text-white text-sm px-5 py-2 font-bold rounded-xl shadow-md shadow-[#1E256F]/15 hover:shadow-lg active:scale-[0.98] transition-all duration-300">
                      Register
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

  {/* Mobile Fullscreen Drawer Panel */}
  <div className={`fixed inset-0 z-50 md:hidden bg-white p-6 flex flex-col h-full transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
  }`}>
    {/* Top section: Heading and Close button */}
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
      <h2 className="text-2xl font-bold text-slate-800 tracking-tight leading-none">Menu</h2>
      <button
        onClick={closeMenu}
        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        aria-label="Close menu"
      >
        <XMarkIcon className="h-6 w-6" />
      </button>
    </div>

    {/* Auth/Profile Actions at the top (Only if authenticated) */}
    {isAuthenticated && (
      <div className="border-b border-slate-100 pb-6 mb-6">
        <div className="border border-slate-200 rounded-2xl p-1.5 transition-all duration-200">
          {/* Collapsible Profile Toggle Button */}
          <button
            onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl hover:bg-slate-50 transition-all focus:outline-none"
          >
            <div className="flex items-center space-x-3 text-left">
              {user?.profile_picture ? (
                <img
                  src={user.profile_picture}
                  alt="Profile"
                  className="w-11 h-11 rounded-full object-cover border border-slate-200 shadow-sm"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-primary-50 flex items-center justify-center border border-primary-100/30 text-primary-600 text-base font-bold uppercase shadow-sm">
                  {getInitials(user?.name, user?.role)}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-bold text-slate-800 text-base leading-tight truncate">{user?.name}</p>
                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <ChevronDownIcon className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${showMobileProfileMenu ? 'rotate-180' : ''}`} />
          </button>

          {/* Collapsible Sub-items (Left Aligned) */}
          {showMobileProfileMenu && (
            <div className="space-y-1 mt-2 mb-2 transition-all duration-200">
              {/* Apply Now and My Applications links hidden for MVP */}
              {isUniversity && (
                <Link
                  to="/profile?tab=applicants"
                  onClick={closeMenu}
                  className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all ${isActive('/profile?tab=applicants')
                    ? 'bg-[#1E256F]/10 text-[#1E256F]'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  Application List
                </Link>
              )}
              <Link
                to={isUniversity ? "/dashboard" : "/profile"}
                onClick={closeMenu}
                className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all ${isActive(isUniversity ? "/dashboard" : "/profile")
                  ? 'bg-[#1E256F]/10 text-[#1E256F]'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {isUniversity ? 'Dashboard' : 'Profile'}
              </Link>
              <Link
                to="/settings"
                onClick={closeMenu}
                className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all ${isActive('/settings')
                  ? 'bg-[#1E256F]/10 text-[#1E256F]'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Settings
              </Link>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 rounded-xl text-base font-semibold text-red-600 hover:bg-red-50 transition-all"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Navigation Links directly below */}
    <div className="space-y-2">
      {navLinks.map((link) => (
        <Link
          key={link.path}
          to={link.path}
          onClick={closeMenu}
          className={`flex items-center justify-between px-4 py-3 rounded-xl text-base transition-all ${isActive(link.path)
            ? 'bg-[#1E256F]/10 text-[#1E256F] font-bold'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-semibold'
          }`}
        >
          <span>{link.name}</span>
          {link.comingSoon && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wide border border-slate-200 leading-none">
              Soon
            </span>
          )}
        </Link>
      ))}
    </div>

    {/* Bottom spacer to align everything */}
    <div className="flex-grow" />

    {/* Auth actions at the absolute bottom (Only if unauthenticated) */}
    {!isAuthenticated && (
      <div className="border-t border-slate-100 pt-6">
        <div className="space-y-3">
          {localStorage.getItem('adminToken') ? (
            <div className="space-y-2">
              <Link to="/supad-panel/dashboard" onClick={closeMenu} className="block text-center py-2.5 rounded-xl bg-red-50 text-red-600 font-bold hover:bg-red-100 transition-all text-sm border border-red-100">
                Admin Panel
              </Link>
              <button onClick={handleAdminLogout} className="w-full text-center py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-all text-sm">
                Admin Logout
              </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-3">
              <Link to="/signup" onClick={closeMenu} className="w-full text-center py-3 rounded-xl bg-[#1E256F] hover:bg-[#161B52] text-white text-base font-bold shadow-md shadow-[#1E256F]/15 active:scale-[0.98] transition-all duration-200">
                Register
              </Link>
              <Link to="/login" onClick={closeMenu} className="w-full text-center py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-all text-sm">
                Log In
              </Link>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
 </nav>
 );
};

export default Navbar;

